import "../foundation/ctl-button.js";
import "../foundation/ctl-typography.js";
import "../foundation/ctl-divider.js";
import "../foundation/ctl-skeleton.js";
import { safeJsonParse } from '../../../utils/safeJsonParse.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --font: var(--ctl-sf-font, var(--ctl-font-sans));
    --text: var(--ctl-sf-text, var(--ctl-color-text));
    --muted: var(--ctl-sf-muted, var(--ctl-color-muted));
    --surface: var(--ctl-sf-surface, var(--ctl-color-surface));
    --border: var(--ctl-sf-border, var(--ctl-color-border));
    --brand: var(--ctl-sf-brand, var(--ctl-color-brand));
    --radius: var(--ctl-sf-radius, var(--ctl-radius-lg));
    --shadow: var(--ctl-sf-shadow, var(--ctl-shadow-sm));
    --focus: var(--ctl-sf-focus-ring, var(--ctl-focus-ring));

    display:block;
    font-family: var(--font);
    color: var(--text);
  }

  .card{
    border: 1px solid var(--border);
    background: var(--surface);
    border-radius: var(--radius);
    box-shadow: var(--shadow);
    overflow:hidden;
  }

  .head{
    padding: var(--ctl-space-3-5) var(--ctl-space-3-5) var(--ctl-space-2-5);
  }

  .titleRow{
    display:flex;
    align-items:flex-start;
    justify-content: space-between;
    gap: var(--ctl-space-2-5);
  }

  .title{
    margin: 0;
    font-weight: var(--ctl-font-weight-ultra);
    letter-spacing: -0.01em;
    font-size: var(--ctl-font-size-lg);
    line-height: 1.15;
  }

  .subtitle{
    margin: var(--ctl-space-1-5) 0 0;
    color: var(--muted);
    font-size: var(--ctl-font-size-md);
    line-height: 1.35;
  }

  .body{
    padding: var(--ctl-space-2-5) var(--ctl-space-3-5) var(--ctl-space-3-5);
    display:grid;
    gap: var(--ctl-space-2-5);
  }

  .items{
    display:grid;
    gap: var(--ctl-space-2);
  }

  .row{
    display:flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--ctl-space-2-5);
  }

  .label{
    min-width:0;
    color: var(--text);
    font-size: var(--ctl-font-size-md);
    line-height: 1.3;
  }

  .note{
    margin-top: var(--ctl-space-0-5);
    color: var(--muted);
    font-size: var(--ctl-font-size-sm);
    line-height: 1.25;
  }

  .value{
    font-size: var(--ctl-font-size-md);
    font-weight: var(--ctl-font-weight-extrabold);
    white-space: nowrap;
  }

  .mutedVal{
    color: var(--muted);
    font-weight: var(--ctl-font-weight-heavy);
  }

  .strong .label{ font-weight: var(--ctl-font-weight-extra-heavy); }
  .strong .value{ font-weight: var(--ctl-font-weight-ultra); }

  .divider{
    height: 1px;
    background: color-mix(in srgb, var(--border) 70%, transparent);
    margin: var(--ctl-space-1) 0;
  }

  .summary{
    border: 1px solid color-mix(in srgb, var(--brand) 18%, var(--border));
    background: color-mix(in srgb, var(--brand) 6%, var(--surface));
    border-radius: var(--ctl-radius-14);
    padding: var(--ctl-space-3);
    display:grid;
    gap: var(--ctl-space-2);
  }

  .summaryTop{
    display:flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--ctl-space-2-5);
  }

  .summaryLabel{
    font-size: var(--ctl-font-size-sm);
    color: var(--muted);
    font-weight: var(--ctl-font-weight-extra-heavy);
    letter-spacing: .02em;
    text-transform: uppercase;
  }

  .summaryAmount{
    font-size: var(--ctl-font-size-xl);
    font-weight: var(--ctl-font-weight-ultra);
    letter-spacing: -0.02em;
    line-height: 1;
  }

  .summaryMeta{
    display:flex;
    gap: var(--ctl-space-2-5);
    flex-wrap: wrap;
    color: var(--muted);
    font-size: var(--ctl-font-size-sm);
  }

  .pill{
    display:inline-flex;
    align-items:center;
    gap: var(--ctl-space-2);
    padding: var(--ctl-space-1-5) var(--ctl-space-2-5);
    border: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
    border-radius: var(--ctl-radius-full);
    background: var(--surface);
    font-weight: var(--ctl-font-weight-heavy);
  }

  .actions{
    display:grid;
    gap: var(--ctl-space-2-5);
  }

  .actions ::slotted(*){ width: 100%; }

  .legal{
    color: var(--muted);
    font-size: var(--ctl-font-size-sm);
    line-height: 1.35;
  }

  .legal a{
    color: color-mix(in srgb, var(--brand) 90%, var(--ctl-color-black));
    text-decoration: underline;
    text-underline-offset: var(--ctl-space-0-5);
  }

  /* loading state */
  :host([loading]) .real{ display:none; }
  .loading{
    display:none;
    gap: var(--ctl-space-2-5);
    padding: var(--ctl-space-2-5) var(--ctl-space-3-5) var(--ctl-space-3-5);
  }
  :host([loading]) .loading{ display:grid; }

  /* disabled */
  :host([disabled]){ opacity: .68; pointer-events:none; }

  /* hide templates */
  ::slotted(template){ display:none; }
</style>

<div class="card" part="card">
  <div class="head" part="header">
    <slot name="header">
      <div class="titleRow">
        <div>
          <div class="title" id="title"></div>
          <div class="subtitle" id="subtitle"></div>
        </div>
      </div>
    </slot>
  </div>

  <div class="body real" part="body">
    <div class="items" part="items">
      <slot name="items"></slot>
      <div id="itemsAuto"></div>
    </div>

    <div class="divider" part="divider"></div>

    <div class="summary" part="summary">
      <div class="summaryTop">
        <div class="summaryLabel" id="summaryLabel">Due today</div>
        <div class="summaryAmount" id="dueToday"></div>
      </div>
      <div class="summaryMeta" part="summaryMeta">
        <span class="pill" id="monthlyPill" hidden><span>Monthly:</span> <span id="monthly"></span></span>
        <span class="pill" id="totalPill" hidden><span>Total:</span> <span id="total"></span></span>
      </div>
      <slot name="footer"></slot>
    </div>

    <div class="actions" part="actions">
      <slot name="actions"></slot>
      <ctl-button id="primaryBtn"></ctl-button>
    </div>

    <div class="legal" part="legal">
      <slot name="legal"></slot>
    </div>
  </div>

  <div class="loading" part="loading">
    <ctl-skeleton variant="rect" height="18px"></ctl-skeleton>
    <ctl-skeleton variant="rect" height="12px" width="68%"></ctl-skeleton>
    <ctl-skeleton variant="rect" height="12px" width="54%"></ctl-skeleton>

    <div style="height:var(--ctl-space-1-5);"></div>

    <ctl-skeleton variant="rect" height="12px" width="92%"></ctl-skeleton>
    <ctl-skeleton variant="rect" height="12px" width="86%"></ctl-skeleton>
    <ctl-skeleton variant="rect" height="12px" width="74%"></ctl-skeleton>

    <div style="height:var(--ctl-space-2-5);"></div>

    <ctl-skeleton variant="rect" height="64px"></ctl-skeleton>
    <ctl-skeleton variant="rect" height="40px"></ctl-skeleton>
  </div>
</div>
`;

function toArray(v){ return Array.isArray(v) ? v : []; }
function clampStr(s){ return String(s ?? "").trim(); }

function formatMoney(value, currencyCode) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currencyCode || "USD", maximumFractionDigits: 2 }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

export class CtlSummaryCard extends HTMLElement {
  static get observedAttributes() {
    return [
      "title",
      "subtitle",
      "currency",
      "items",
      "total",
      "monthly",
      "due-today",
      "primary-label",
      "loading",
      "disabled"
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._title = this.shadowRoot.getElementById("title");
    this._subtitle = this.shadowRoot.getElementById("subtitle");

    this._itemsAuto = this.shadowRoot.getElementById("itemsAuto");

    this._summaryLabel = this.shadowRoot.getElementById("summaryLabel");
    this._dueToday = this.shadowRoot.getElementById("dueToday");
    this._monthly = this.shadowRoot.getElementById("monthly");
    this._total = this.shadowRoot.getElementById("total");
    this._monthlyPill = this.shadowRoot.getElementById("monthlyPill");
    this._totalPill = this.shadowRoot.getElementById("totalPill");

    this._primaryBtn = this.shadowRoot.getElementById("primaryBtn");

    this._items = [];
    this._onPrimary = this._onPrimary.bind(this);
  }

  connectedCallback() {
    if (!this.hasAttribute("title")) this.setAttribute("title", "Summary");
    if (!this.hasAttribute("subtitle")) this.setAttribute("subtitle", "Review charges before reserving.");
    if (!this.hasAttribute("currency")) this.setAttribute("currency", "USD");
    if (!this.hasAttribute("primary-label")) this.setAttribute("primary-label", "Continue");
    if (!this.hasAttribute("due-today")) this.setAttribute("due-today", "0");

    // hydrate items
    if (this.hasAttribute("items")) {
      const parsed = safeJsonParse(this.getAttribute("items"), []);
      if (Array.isArray(parsed)) this._items = parsed;
    }

    this._primaryBtn.addEventListener("click", this._onPrimary);
    this._sync();
  }

  disconnectedCallback() {
    this._primaryBtn.removeEventListener("click", this._onPrimary);
  }

  attributeChangedCallback(name) {
    if (name === "items") {
      const parsed = safeJsonParse(this.getAttribute("items"), []);
      this._items = Array.isArray(parsed) ? parsed : [];
    }
    this._sync();
  }

  // ---- Props ----
  get title(){ return this.getAttribute("title") || ""; }
  set title(v){ this.setAttribute("title", String(v ?? "")); }

  get subtitle(){ return this.getAttribute("subtitle") || ""; }
  set subtitle(v){ this.setAttribute("subtitle", String(v ?? "")); }

  get currency(){ return this.getAttribute("currency") || "USD"; }
  set currency(v){ this.setAttribute("currency", String(v ?? "USD")); }

  get items(){ return this._items; }
  set items(v){
    this._items = toArray(v);
    this.setAttribute("items", JSON.stringify(this._items));
    this._syncItems();
  }

  get total(){ return this.getAttribute("total") || ""; }
  set total(v){ v == null ? this.removeAttribute("total") : this.setAttribute("total", String(v)); }

  get monthly(){ return this.getAttribute("monthly") || ""; }
  set monthly(v){ v == null ? this.removeAttribute("monthly") : this.setAttribute("monthly", String(v)); }

  get dueToday(){ return this.getAttribute("due-today") || ""; }
  set dueToday(v){ this.setAttribute("due-today", String(v ?? "")); }

  get primaryLabel(){ return this.getAttribute("primary-label") || "Continue"; }
  set primaryLabel(v){ this.setAttribute("primary-label", String(v ?? "Continue")); }

  get loading(){ return this.hasAttribute("loading"); }
  set loading(v){ v ? this.setAttribute("loading","") : this.removeAttribute("loading"); }

  get disabled(){ return this.hasAttribute("disabled"); }
  set disabled(v){ v ? this.setAttribute("disabled","") : this.removeAttribute("disabled"); }

  // ---- Render ----
  _sync() {
    this._title.textContent = this.title;
    this._subtitle.textContent = this.subtitle;

    this._primaryBtn.textContent = this.primaryLabel;
    this._primaryBtn.toggleAttribute("disabled", this.disabled || this.loading);
    this._primaryBtn.toggleAttribute("loading", this.loading);

    this._syncItems();

    const cur = this.currency;
    const due = formatMoney(this.dueToday, cur);
    this._dueToday.textContent = due || "—";

    const monthly = clampStr(this.monthly);
    const total = clampStr(this.total);

    if (monthly) {
      this._monthly.textContent = formatMoney(monthly, cur) || monthly;
      this._monthlyPill.hidden = false;
    } else {
      this._monthlyPill.hidden = true;
      this._monthly.textContent = "";
    }

    if (total) {
      this._total.textContent = formatMoney(total, cur) || total;
      this._totalPill.hidden = false;
    } else {
      this._totalPill.hidden = true;
      this._total.textContent = "";
    }
  }

  _syncItems() {
    // If user provides slot="items", we still render auto items below it.
    const cur = this.currency;
    const items = toArray(this._items);

    const html = items.map((it) => {
      const divider = !!it.divider;
      if (divider) return `<div class="divider" part="itemDivider"></div>`;

      const label = clampStr(it.label);
      const note = clampStr(it.note);
      const strong = !!it.strong;
      const muted = !!it.muted;

      let valueText = "";
      if (it.money === true) valueText = formatMoney(it.value, cur);
      else valueText = clampStr(it.value);

      const rowCls = ["row", strong ? "strong" : ""].filter(Boolean).join(" ");
      const valCls = ["value", muted ? "mutedVal" : ""].filter(Boolean).join(" ");

      return `
        <div class="${rowCls}" part="itemRow">
          <div class="label" part="itemLabel">
            ${label ? this._esc(label) : ""}
            ${note ? `<div class="note" part="itemNote">${this._esc(note)}</div>` : ""}
          </div>
          <div class="${valCls}" part="itemValue">${this._esc(valueText || "—")}</div>
        </div>
      `;
    }).join("");

    this._itemsAuto.innerHTML = html;
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  // ---- Events ----
  _onPrimary(e) {
    e.preventDefault();
    if (this.disabled || this.loading) return;

    const detail = {
      currency: this.currency,
      items: [...toArray(this._items)],
      dueToday: this.dueToday || null,
      monthly: this.monthly || null,
      total: this.total || null
    };

    this.dispatchEvent(new CustomEvent("ctl-click", {
      bubbles: true,
      composed: true,
      detail
    }));
  }
}

customElements.define("ctl-summary-card", CtlSummaryCard);
